import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, X, Trash2, UserCog, Tag } from "lucide-react";
import { http, defaultStatuses, money, notify, can, maskMobile } from "../api";
import { Status, Modal } from "./common";
import { LeadForm } from "./forms";
import { LoginStatusSummary, mapBucketToStatus } from "./LoginStatusSummary";

export default function Leads({ onSelect, refresh, user, initialFilters, onClearFilters }) {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [reviewOnly, setReviewOnly] = useState(false);
  const [activeBucket, setActiveBucket] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkModal, setBulkModal] = useState(null); // 'assign' | 'status'
  const latest = useRef(0);
  const assignedTo = initialFilters?.assigned_to || "";
  const assignedName = initialFilters?.assigned_name || "";

  const load = () => {
    const id = ++latest.current;
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (status) params.set("status", status);
    if (assignedTo) params.set("assigned_to", assignedTo);
    if (reviewOnly) params.set("review", "pending");
    const t = setTimeout(
      () =>
        http
          .get(`/leads?${params.toString()}`)
          .then((r) => {
            if (id === latest.current) {
              setItems(r.data);
              const ids = new Set(r.data.map((x) => x.lead_id));
              setSelected((prev) => new Set([...prev].filter((x) => ids.has(x))));
            }
          })
          .finally(() => setLoading(false)),
      180,
    );
    return () => clearTimeout(t);
  };

  useEffect(load, [search, status, refresh, assignedTo, reviewOnly]);

  // Strict RBAC (Iteration 14): only admins can bulk-assign or bulk-delete
  // leads; only admins + back office roles can bulk-change status.
  const isAdmin = user.role === "admin";
  const isBackOffice = user.role === "back_office_manager" || user.role === "back_office_executive";
  const canBulkAssign = isAdmin;
  const canBulkStatus = isAdmin || isBackOffice;
  const canBulkDelete = isAdmin;

  const toggle = (leadId, event) => {
    event.stopPropagation();
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId); else next.add(leadId);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((x) => x.lead_id)));
  };

  const clearSelection = () => setSelected(new Set());

  const bulkDelete = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} lead${ids.length === 1 ? "" : "s"}? You'll have 30 seconds to undo.`)) return;
    try {
      const r = await http.post("/leads/bulk/delete", { lead_ids: ids });
      const { restore_id, deleted, cascade } = r.data;
      clearSelection();
      load();
      const cascadeBits = [];
      if (cascade?.applications) cascadeBits.push(`${cascade.applications} app${cascade.applications === 1 ? "" : "s"}`);
      if (cascade?.documents) cascadeBits.push(`${cascade.documents} doc${cascade.documents === 1 ? "" : "s"}`);
      if (cascade?.followups) cascadeBits.push(`${cascade.followups} follow-up${cascade.followups === 1 ? "" : "s"}`);
      showUndoToast({
        restoreId: restore_id,
        summary: `${deleted} lead${deleted === 1 ? "" : "s"} deleted${cascadeBits.length ? ` · ${cascadeBits.join(", ")}` : ""}`,
        onRestored: load,
      });
    } catch (err) {
      notify.error(err, "Bulk delete failed");
    }
  };

  const onCardBucket = (bucket) => {
    setActiveBucket(bucket);
    setStatus(bucket ? (mapBucketToStatus[bucket] || "") : "");
  };

  return (
    <>
      <div className="page-intro">
        <div>
          <p className="eyebrow">RECORDS / SALES</p>
          <h1>Login Status</h1>
          <p className="muted">Every customer conversation, clearly owned.</p>
        </div>
        {can(user, "create_leads") && (
          <button
            data-testid="leads-create-button"
            className="primary-button"
            onClick={() => setShowForm(true)}
          >
            <Plus size={17} /> Create lead
          </button>
        )}
      </div>

      <LoginStatusSummary
        user={user}
        activeBucket={activeBucket}
        onBucketChange={onCardBucket}
      />

      <div className="toolbar">
        <div className="table-search">
          <Search size={16} />
          <input
            data-testid="leads-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, mobile, PAN, or Lead ID"
          />
        </div>
        <select
          data-testid="leads-status-filter"
          value={status}
          onChange={(e) => { setStatus(e.target.value); setActiveBucket(""); }}
        >
          <option value="">All statuses</option>
          {defaultStatuses.map((x) => (
            <option key={x}>{x}</option>
          ))}
        </select>
        <button
          data-testid="leads-review-filter-button"
          className={`outline-button ${reviewOnly ? "active" : ""}`}
          onClick={() => setReviewOnly((v) => !v)}
          title="Show only leads still awaiting your review before Send-to-Login"
          style={reviewOnly ? { background: "#fef3c7", borderColor: "#b45309", color: "#b45309" } : undefined}
        >
          For Review
        </button>
        <button
          data-testid="leads-filter-button"
          className="outline-button"
          onClick={() => {
            setStatus("");
            setActiveBucket("");
            setSearch("");
            setReviewOnly(false);
            onClearFilters?.();
          }}
        >
          Clear filters
        </button>
        {assignedName && (
          <span className="filter-chip" data-testid="leads-owner-filter-chip">
            Owned by <strong>{assignedName}</strong>
            <button
              className="chip-close"
              onClick={() => onClearFilters?.()}
              aria-label="Clear owner filter"
              data-testid="leads-owner-filter-clear"
            >
              <X size={12} />
            </button>
          </span>
        )}
        <span className="record-count">{loading ? "Loading…" : `${items.length} records`}</span>
      </div>

      <div data-testid="leads-table" className="table-wrap">
        <table>
          <caption className="sr-only">Permitted leads</caption>
          <thead>
            <tr>
              <th className="cell-select">
                <input
                  type="checkbox"
                  data-testid="leads-select-all"
                  checked={items.length > 0 && selected.size === items.length}
                  onChange={toggleAll}
                  aria-label="Select all"
                />
              </th>
              <th>Lead / customer</th>
              <th>Contact</th>
              <th>Loan request</th>
              <th>Bank name</th>
              <th>Owner / team</th>
              <th>Status</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {items.map((x) => (
              <tr
                data-testid={`leads-table-row-${x.lead_id}`}
                key={x.lead_id}
                onClick={() => onSelect(x.lead_id)}
                className={selected.has(x.lead_id) ? "clickable-row selected" : "clickable-row"}
              >
                <td className="cell-select" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    data-testid={`leads-select-${x.lead_id}`}
                    checked={selected.has(x.lead_id)}
                    onChange={(e) => toggle(x.lead_id, e)}
                    aria-label={`Select ${x.customer_name}`}
                  />
                </td>
                <td>
                  <strong className="mono">{x.lead_id}</strong>
                  <span className="cell-sub">{x.customer_name}</span>
                </td>
                <td data-label="Contact">
                  <span>{maskMobile(x.mobile)}</span>
                  <span className="cell-sub">{x.city}</span>
                </td>
                <td data-label="Loan">
                  <span>{x.loan_type}</span>
                  <span className="cell-sub">{money(x.loan_amount)}</span>
                </td>
                <td data-label="Bank" data-testid={`leads-bank-${x.lead_id}`}>
                  {x.bank_name ? (
                    <span>{x.bank_name}</span>
                  ) : (
                    <span className="muted" style={{ fontStyle: "italic" }}>Not selected</span>
                  )}
                </td>
                <td data-label="Owner">
                  <span>{x.assigned_name || "Assigned owner"}</span>
                  <span className="cell-sub">{x.sales_team_name}</span>
                </td>
                <td data-label="Status">
                  <Status value={x.status} />
                  {x.review_status === "pending_review" && (
                    <span
                      data-testid={`leads-review-badge-${x.lead_id}`}
                      style={{
                        marginLeft: 6, border: "1px solid #b45309",
                        color: "#b45309", background: "#fef3c7",
                        padding: "2px 6px", fontSize: 9, fontWeight: 700,
                        letterSpacing: ".1em",
                      }}
                    >
                      FOR REVIEW
                    </span>
                  )}
                </td>
                <td data-label="Updated" className="muted">{x.updated_at?.slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!items.length && !loading && <div className="empty">No leads match this view.</div>}
      </div>

      {selected.size > 0 && (
        <div className="bulk-bar" data-testid="bulk-action-bar" role="status">
          <button className="icon-button" onClick={clearSelection} aria-label="Clear selection">
            <X size={16} />
          </button>
          <strong>
            {selected.size} lead{selected.size === 1 ? "" : "s"} selected
          </strong>
          <div className="bulk-actions">
            {canBulkAssign && (
              <button
                data-testid="bulk-assign-button"
                className="outline-button"
                onClick={() => setBulkModal("assign")}
              >
                <UserCog size={14} /> Reassign
              </button>
            )}
            {canBulkStatus && (
              <button
                data-testid="bulk-status-button"
                className="outline-button"
                onClick={() => setBulkModal("status")}
              >
                <Tag size={14} /> Change status
              </button>
            )}
            {canBulkDelete && (
              <button
                data-testid="bulk-delete-button"
                className="outline-button danger-button"
                onClick={bulkDelete}
              >
                <Trash2 size={14} /> Delete
              </button>
            )}
          </div>
        </div>
      )}

      {bulkModal === "assign" && (
        <BulkAssignModal
          leadIds={[...selected]}
          user={user}
          onClose={() => setBulkModal(null)}
          onDone={() => {
            setBulkModal(null);
            clearSelection();
            load();
          }}
        />
      )}
      {bulkModal === "status" && (
        <BulkStatusModal
          leadIds={[...selected]}
          onClose={() => setBulkModal(null)}
          onDone={() => {
            setBulkModal(null);
            clearSelection();
            load();
          }}
        />
      )}

      {showForm && (
        <LeadForm
          user={user}
          onClose={() => setShowForm(false)}
          onSaved={() => {
            setShowForm(false);
            load();
          }}
        />
      )}
    </>
  );
}

function BulkAssignModal({ leadIds, user, onClose, onDone }) {
  const [members, setMembers] = useState([]);
  const [assignee, setAssignee] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    http.get("/users").then((r) => {
      // Sales manager restricted to own team by backend already
      const options = r.data.filter((u) => ["telecaller", "sales_manager"].includes(u.role) && u.active !== false);
      setMembers(options);
      if (options.length) setAssignee(options[0].id);
    }).catch(() => {});
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await http.post("/leads/bulk/assign", { lead_ids: leadIds, assigned_to: assignee });
      const name = members.find((m) => m.id === assignee)?.name || "the new owner";
      notify.success(`${r.data.updated} lead${r.data.updated === 1 ? "" : "s"} reassigned to ${name}`);
      onDone();
    } catch (err) {
      notify.error(err, "Bulk reassignment failed");
      setBusy(false);
    }
  };

  return (
    <Modal title={`Reassign ${leadIds.length} lead${leadIds.length === 1 ? "" : "s"}`} onClose={onClose}>
      <form className="record-form" onSubmit={submit}>
        <label>
          New owner
          <select
            data-testid="bulk-assign-user-select"
            value={assignee}
            onChange={(e) => setAssignee(e.target.value)}
            required
          >
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name} — {m.role.replaceAll("_", " ")} · {m.team_name}
              </option>
            ))}
          </select>
        </label>
        <p className="upload-note">
          The selected leads will move to this owner&apos;s team scope.
        </p>
        <div className="form-actions">
          <button type="button" className="outline-button" onClick={onClose}>Cancel</button>
          <button
            data-testid="bulk-assign-submit"
            className="primary-button"
            disabled={!assignee || busy}
          >
            {busy ? "Reassigning…" : "Reassign leads"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function BulkStatusModal({ leadIds, onClose, onDone }) {
  const [value, setValue] = useState("Contacted");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = reason.trim();
    if (!trimmed) {
      notify.error(null, "Add a reason so the audit trail knows why the status changed.");
      return;
    }
    setBusy(true);
    try {
      const r = await http.post("/leads/bulk/status", { lead_ids: leadIds, status: value, reason: trimmed });
      notify.success(`${r.data.updated} lead${r.data.updated === 1 ? "" : "s"} moved to ${value}`);
      onDone();
    } catch (err) {
      notify.error(err, "Bulk status update failed");
      setBusy(false);
    }
  };

  return (
    <Modal title={`Change status for ${leadIds.length} lead${leadIds.length === 1 ? "" : "s"}`} onClose={onClose}>
      <form className="record-form" onSubmit={submit}>
        <label>
          New status
          <select
            data-testid="bulk-status-select"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          >
            {defaultStatuses.map((s) => <option key={s}>{s}</option>)}
          </select>
        </label>
        <label>
          <span>Reason for change <span className="required-mark">*</span></span>
          <textarea
            data-testid="bulk-status-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why are these leads moving to this status?"
            rows={3}
            required
          />
        </label>
        <div className="form-actions">
          <button type="button" className="outline-button" onClick={onClose}>Cancel</button>
          <button
            data-testid="bulk-status-submit"
            className="primary-button"
            disabled={busy || !reason.trim()}
          >
            {busy ? "Updating…" : "Update status"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Show a toast that offers to undo a soft delete within the server-side 30s window.
 * `onRestored` is called once the user picks Undo and the server confirms restore.
 */
export function showUndoToast({ restoreId, summary, onRestored }) {
  toast(summary, {
    duration: 29000,
    action: {
      label: "Undo",
      onClick: async () => {
        try {
          await http.post(`/leads/restore/${restoreId}`);
          notify.success("Restored — everything is back exactly as it was.");
          onRestored?.();
        } catch (err) {
          notify.error(err, "Restore failed — the undo window may have expired.");
        }
      },
    },
  });
}
